// @ts-check
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../shared/Modal.jsx';
import MfaSetupModal from './MfaSetupModal.jsx';
import { callStepUpVerify, callMfaEnrollStart } from '../../api/mfaApi.js';
import { getSessionToken, setSessionToken } from '../../api/sessionToken.js';
import { registerStepUpHandler, setStepUpToken } from '../../api/stepUp.js';

/**
 * StepUpDialog — the one shared §16 re-verify dialog (CLAUDE.md "Step-Up
 * Authentication Policy", docs/step-up-auth-gate-table.md).
 *
 * Mounted once in the tenant app shell. It registers itself with
 * src/api/stepUp.js; when a gated endpoint answers `403 { stepUp: 'required' }`
 * the API client asks it to run, and retries the request once the user has
 * re-verified. The resulting token lives in memory only (src/api/stepUp.js).
 *
 * Steps:
 *   loading  — step-up-verify `begin`
 *   totp     — enter a code from the enrolled authenticator app
 *   webauthn — confirm the enrolled passkey
 *   enrol-password / enrol-setup — the user has no factor yet: prove the
 *              password (mfa-enroll), register one (mfa-setup), then come back
 *              here and verify with it. Never a hard block.
 *   recovery — enrolled but the factor is unusable; points to recovery.
 *
 * Layering (#7985): step-up interrupts whatever the user is doing, usually from
 * inside an open modal, so it must sit above every modal and overlay no matter
 * where this component is mounted. It renders into its own host element
 * appended to <body> when it opens: no ancestor's `pointer-events: none` (the
 * shell mounts it inside TopBannerStack), transform or stacking context can
 * reach it, and the host's z-index is the largest a browser accepts, so no
 * modal-stack depth or overlay z-index can outrank it; a tie would lose to the
 * host on document order, since it is appended last.
 *
 * The tenant app mounts it with `tenantId`; the GRC auditor app passes its own
 * `api` (auditor-step-up-verify, auditor-mfa-enroll, its own enrolment screen)
 * — one dialog, two principals (FDNA-02 D2). The super-admin portal passes a
 * third (FDNA-02b), whose `renderEnrolProof` replaces the password step with
 * its own identity proof (a fresh Google sign-in, or the password).
 *
 * @typedef {{
 *   begin: () => Promise<any>,
 *   verify: (body: object) => Promise<any>,
 *   enrolStart: (password: string) => Promise<any>,
 *   adoptSession: (token: string, user?: any) => void,
 *   renderEnrolSetup: (p: { challengeToken: string, onDone: (token: string, user?: any) => void, onCancel: () => void }) => import('react').ReactNode,
 *   renderEnrolProof?: (p: { onChallenge: (challengeToken: string) => void, onCancel: () => void }) => import('react').ReactNode,
 *   recoveryHint: string,
 * }} StepUpApi
 *
 * @param {{ tenantId?: string | null, api?: StepUpApi }} props
 */
export default function StepUpDialog({ tenantId, api: apiProp }) {
  const api = useMemo(() => apiProp || tenantStepUpApi(tenantId), [apiProp, tenantId]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('loading');
  const [totpTag, setTotpTag] = useState(/** @type {string|null} */ (null));
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState(/** @type {string|null} */ (null));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** @type {import('react').MutableRefObject<((ok: boolean) => void) | null>} */
  const resolver = useRef(null);
  /** @type {import('react').MutableRefObject<{ authenticationOptions: any, authToken: string } | null>} */
  const webauthnBegin = useRef(null);

  const finish = useCallback((/** @type {boolean} */ ok) => {
    const r = resolver.current;
    resolver.current = null;
    setOpen(false);
    setCode('');
    setPassword('');
    setChallengeToken(null);
    setError('');
    setBusy(false);
    if (r) r(ok);
  }, []);

  const begin = useCallback(async () => {
    setStep('loading');
    setError('');
    const res = await api.begin();
    if (res.stepUp === 'enrol') {
      setStep('enrol-password');
      return;
    }
    if (res.stepUp === 'recovery') {
      setStep('recovery');
      return;
    }
    if (res.error) {
      setError(res.error);
      setStep('failed');
      return;
    }
    if (res.method === 'totp') {
      setTotpTag(res.totpTag || null);
      setStep('totp');
      return;
    }
    webauthnBegin.current = {
      authenticationOptions: res.authenticationOptions,
      authToken: res.authToken,
    };
    setStep('webauthn');
  }, [api]);

  useEffect(() => {
    const unregister = registerStepUpHandler(
      () =>
        new Promise((resolve) => {
          resolver.current = resolve;
          setOpen(true);
          begin();
        })
    );
    return () => {
      unregister();
      // Unmounted mid-verification (navigation, sign-out): release the waiting
      // request as cancelled, or every later gated call would queue behind it.
      const r = resolver.current;
      resolver.current = null;
      if (r) r(false);
    };
  }, [begin]);

  /** @param {{ stepUpToken?: string, expiresAt?: string, error?: string }} res */
  function accept(res) {
    if (res.error || !res.stepUpToken || !res.expiresAt) {
      setError(res.error || 'Verification failed. Please try again.');
      setBusy(false);
      return;
    }
    setStepUpToken(res.stepUpToken, res.expiresAt);
    finish(true);
  }

  async function submitCode(/** @type {import('react').FormEvent} */ e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setError('');
    accept(await api.verify({ code: code.trim() }));
    setCode('');
  }

  async function confirmPasskey() {
    if (!webauthnBegin.current) return;
    setBusy(true);
    setError('');
    try {
      const { startAuthentication } = await import('@simplewebauthn/browser');
      const authenticationResponse = await startAuthentication({
        optionsJSON: webauthnBegin.current.authenticationOptions,
      });
      accept(
        await api.verify({
          authToken: webauthnBegin.current.authToken,
          authenticationResponse,
          origin: window.location.origin,
        })
      );
    } catch (err) {
      setBusy(false);
      // A used challenge cannot be retried — fetch a fresh one.
      await begin();
      setError(
        /** @type {any} */ (err)?.name === 'NotAllowedError'
          ? 'The passkey prompt was cancelled or timed out. Try again.'
          : 'Passkey verification failed. Try again.'
      );
    }
  }

  async function submitPassword(/** @type {import('react').FormEvent} */ e) {
    e.preventDefault();
    if (!password) {
      setError('Enter your password to continue.');
      return;
    }
    setBusy(true);
    setError('');
    const res = await api.enrolStart(password);
    setBusy(false);
    setPassword('');
    if (res.error) {
      setError(res.error);
      return;
    }
    setChallengeToken(res.challengeToken);
    setStep('enrol-setup');
  }

  /** Enrolment issued a fresh session — adopt it, then verify with the new factor. */
  function onEnrolled(/** @type {string} */ token, /** @type {any} */ user) {
    if (token) api.adoptSession(token, user);
    setChallengeToken(null);
    begin();
  }

  if (!open) return null;

  const hint = {
    fontSize: 12,
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 16,
    lineHeight: 1.5,
  };

  return (
    <StepUpLayer>
      <Modal title="Confirm it's you" onClose={() => finish(false)} dirty={false}>
        <div data-testid="step-up-dialog" style={{ maxWidth: 440 }}>
          {error && (
            <div
              role="alert"
              style={{
                padding: '8px 12px',
                marginBottom: 12,
                borderRadius: 8,
                background: 'var(--bg3)',
                border: '1px solid var(--danger)',
                color: 'var(--danger)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {step === 'loading' && <p style={hint}>Preparing verification…</p>}

          {step === 'totp' && (
            <form onSubmit={submitCode}>
              <p style={hint}>
                This action needs a fresh check of your second factor. Enter the current code from
                your authenticator app
                {totpTag ? ` (the Tryggr entry ending ${totpTag})` : ''}. You will not be asked
                again for 10 minutes.
              </p>
              <input
                className="input"
                data-testid="step-up-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                aria-label="Authenticator code"
                style={{ width: '100%', fontFamily: 'var(--font-mono)', letterSpacing: '0.3em' }}
              />
              <Actions busy={busy} onCancel={() => finish(false)} label="Verify" submit />
            </form>
          )}

          {step === 'webauthn' && (
            <div>
              <p style={hint}>
                This action needs a fresh check of your second factor. Confirm with your passkey.
                You will not be asked again for 10 minutes.
              </p>
              <Actions
                busy={busy}
                onCancel={() => finish(false)}
                label="Use passkey"
                onConfirm={confirmPasskey}
              />
            </div>
          )}

          {step === 'enrol-password' &&
            api.renderEnrolProof &&
            api.renderEnrolProof({
              onChallenge: (token) => {
                setChallengeToken(token);
                setStep('enrol-setup');
              },
              onCancel: () => finish(false),
            })}

          {step === 'enrol-password' && !api.renderEnrolProof && (
            <form onSubmit={submitPassword}>
              <p style={hint}>
                This action needs two-step verification, which is not set up on your account yet.
                Enter your password to set it up now — afterwards you will sign in with it instead
                of your password.
              </p>
              <input
                className="input"
                data-testid="step-up-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-label="Password"
                style={{ width: '100%' }}
              />
              <Actions busy={busy} onCancel={() => finish(false)} label="Continue" submit />
            </form>
          )}

          {step === 'enrol-setup' &&
            challengeToken &&
            api.renderEnrolSetup({
              challengeToken,
              onDone: onEnrolled,
              onCancel: () => finish(false),
            })}

          {step === 'recovery' && (
            <div>
              <p style={hint}>{api.recoveryHint}</p>
              <Actions busy={false} onCancel={() => finish(false)} label={null} />
            </div>
          )}

          {step === 'failed' && (
            <Actions
              busy={busy}
              onCancel={() => finish(false)}
              label="Try again"
              onConfirm={begin}
            />
          )}
        </div>
      </Modal>
    </StepUpLayer>
  );
}

/**
 * @param {{ busy: boolean, onCancel: () => void, label: string | null, submit?: boolean, onConfirm?: () => void }} props
 */
function Actions({ busy, onCancel, label, submit, onConfirm }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
      <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      {label && (
        <button
          type={submit ? 'submit' : 'button'}
          className="btn btn-primary"
          data-testid="step-up-confirm"
          onClick={submit ? undefined : onConfirm}
          disabled={busy}
        >
          {busy ? 'Verifying…' : label}
        </button>
      )}
    </div>
  );
}

/**
 * The tenant-user step-up API (step-up-verify, mfa-enroll, mfa-setup).
 * @param {string | null | undefined} tenantId
 * @returns {StepUpApi}
 */
function tenantStepUpApi(tenantId) {
  return {
    begin: () => callStepUpVerify('begin', {}, getSessionToken()),
    verify: (body) => callStepUpVerify('verify', body, getSessionToken()),
    enrolStart: (password) => callMfaEnrollStart(password, getSessionToken()),
    // mfa-setup voided the password and issued a fresh session JWT.
    adoptSession: (token) => {
      setSessionToken(token);
      try {
        if (tenantId) localStorage.setItem(`seasuite_token_${tenantId}`, token);
      } catch {
        /* storage unavailable — the in-memory session still holds the new token */
      }
    },
    renderEnrolSetup: ({ challengeToken, onDone, onCancel }) => (
      <MfaSetupModal
        challengeToken={challengeToken}
        functionsBaseUrl={undefined}
        onComplete={onDone}
        onBack={onCancel}
      />
    ),
    recoveryHint:
      'Your authenticator cannot be used. Sign out and use “Lost your authenticator?” on the sign-in page to recover your account, then try again.',
  };
}

/** Largest z-index browsers accept (a signed 32-bit integer). */
const TOP_Z_INDEX = 2147483647;

/**
 * Portals its children into a host element appended to <body> for as long as
 * it is mounted. See "Layering" above.
 * @param {{ children: import('react').ReactNode }} props
 */
function StepUpLayer({ children }) {
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.setAttribute('data-step-up-layer', '');
    el.style.position = 'relative';
    el.style.zIndex = String(TOP_Z_INDEX);
    el.style.pointerEvents = 'auto';
    return el;
  });
  useEffect(() => {
    document.body.appendChild(host);
    return () => {
      host.remove();
    };
  }, [host]);
  return createPortal(children, host);
}
